import { useMemo, useState } from 'react';
import type { CapabilityCode } from '@railroute/contracts/core';
import { fetchMemberships } from '../api';
import { mayEditPosition, mayManageClearanceEvidence } from '../sections';
import {
  admissionLabel,
  countByTone,
  decisionClearance,
  countWithoutBasis,
  filterRows,
  withoutBasisCount,
  localToday,
  relativeTerm,
  rowTone,
  sortRows,
  subdivisionCell,
  termClass,
  formatDate,
  type DecisionKey,
  type SortOrder,
  type ToneFilter,
} from './personnel-rows';
import { useResource } from './useResource';
import { WorkerCard } from './WorkerCard';

// Персонал организации — главный рабочий экран инженера по охране труда.
// Экран отвечает на вопрос, ради которого его открывают: у кого проблема. За
// секунду, а не построчным чтением. Поэтому:
//
// — строка целиком окрашена тем же светофором, что и в мобильном приложении:
//   человек переходит с телефона на компьютер и не переучивается;
// — колонок четыре, по которым принимают решение: допуск к работе,
//   медосмотр, повторный инструктаж, группа по электробезопасности. Всё
//   остальное — в карточке по нажатию на строку;
// — проблемные сверху, а счётчики над таблицей говорят, сколько их, до того,
//   как глаз дошёл до первой строки.
//
// ЦВЕТ НЕ ЕДИНСТВЕННЫЙ ПРИЗНАК. Слово остаётся в каждой строке — для того,
// кто плохо различает цвета, и для распечатки, где заливки не видно.
//
// ЧТО ПРОСРОЧЕНО, ЧТО ИСТЕКАЕТ, ДОПУЩЕН ЛИ ЧЕЛОВЕК — РЕШАЕТ СЕРВЕР, тем же
// расчётом, что и готовность бригады. Панель эти состояния не пересчитывает:
// от даты она считает только «через сколько», а слово берёт у сервера.
//
// КОГО ВООБЩЕ ВИДНО — ТОЖЕ РЕШАЕТ СЕРВЕР. Инженеру по охране труда он отдаёт
// всю организацию, руководителю подразделения — его подразделение. Отбор на
// экране только сужает уже разрешённый список по выбору человека.

const DECISIONS: { key: DecisionKey; title: string }[] = [
  { key: 'medical', title: 'Медосмотр' },
  { key: 'repeatBriefing', title: 'Повторный инструктаж' },
  { key: 'electrical', title: 'Группа по электробезопасности' },
];

const FILTERS: { value: ToneFilter; label: (counts: ReturnType<typeof countByTone>) => string }[] = [
  { value: 'danger', label: (c) => `Не допущено · ${c.danger}` },
  { value: 'warn', label: (c) => `Истекают сроки · ${c.warn}` },
  { value: 'ok', label: (c) => `Допущено · ${c.ok}` },
];

export function PersonnelSection({
  token,
  organizationId,
  capabilities,
  onUnauthorized,
}: {
  token: string;
  organizationId: string;
  /**
   * Issue #524: разделу нужны не только его собственные права — ввод и
   * продление допуска внутри карточки держатся на `CLEARANCE_EVIDENCE_MANAGE`,
   * как сам раздел держится на `PERSONNEL_READ`.
   */
  capabilities: readonly CapabilityCode[];
  onUnauthorized: () => void;
}) {
  const people = useResource(
    () => fetchMemberships(token, organizationId, true),
    onUnauthorized,
    [token, organizationId],
  );
  const [tone, setTone] = useState<ToneFilter>('all');
  const [query, setQuery] = useState('');
  const [order, setOrder] = useState<SortOrder>('problems');
  // Открытый работник помнится НОМЕРОМ СТРОКИ, а не самой строкой: после ввода
  // допуска список перечитывается у сервера, и запомненная строка осталась бы
  // прежней — карточка показывала бы «Не допущен» под только что внесённым
  // документом.
  const [openedId, setOpenedId] = useState<string | null>(null);

  const all = people.data ?? [];
  const counts = useMemo(() => countByTone(all), [all]);
  const noBasisCount = useMemo(() => countWithoutBasis(all), [all]);
  const shown = useMemo(() => sortRows(filterRows(all, { tone, query }), order), [all, tone, query, order]);
  const today = localToday();
  const opened = all.find((row) => row.id === openedId) ?? null;

  if (people.loading && people.data === null) return <p className="section__note">Загружаю список…</p>;
  if (people.error) return <p className="ui-error">{people.error}</p>;
  if (all.length === 0) return <p className="section__note">В организации не числится ни одного работника.</p>;

  const noRequirements = all.every((row) => row.clearances.length === 0);

  return (
    <>
      <div className="personnel__toolbar screen-only">
        <div className="personnel__counters" role="group" aria-label="Отбор по состоянию">
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              className={`counter counter--${filter.value}${tone === filter.value ? ' counter--active' : ''}`}
              aria-pressed={tone === filter.value}
              onClick={() => setTone(tone === filter.value ? 'all' : filter.value)}
            >
              {filter.label(counts)}
            </button>
          ))}
          <button
            type="button"
            className={`counter counter--problems${tone === 'problems' ? ' counter--active' : ''}`}
            aria-pressed={tone === 'problems'}
            onClick={() => setTone(tone === 'problems' ? 'all' : 'problems')}
          >
            Только с проблемами
          </button>
          {/* Issue #537: виден всем, кто видит список; дозаполнять основание
              может только ответственный за допуски — в карточке работника. */}
          <button
            type="button"
            className={`counter counter--no-basis${tone === 'noBasis' ? ' counter--active' : ''}`}
            aria-pressed={tone === 'noBasis'}
            onClick={() => setTone(tone === 'noBasis' ? 'all' : 'noBasis')}
          >
            Допуски без документа · {noBasisCount}
          </button>
        </div>
        <select
          className="ui-input personnel__order"
          value={order}
          onChange={(event) => setOrder(event.target.value as SortOrder)}
          aria-label="Порядок строк"
        >
          <option value="problems">Сначала проблемные</option>
          <option value="name">По фамилии</option>
        </select>
        <input
          className="ui-input personnel__search"
          type="search"
          placeholder="Фамилия или табельный номер"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Поиск по фамилии или табельному номеру"
        />
      </div>

      {shown.length === 0 ? (
        <p className="section__note">
          {tone === 'all' ? 'Никого не нашлось.' : 'В этом отборе никого нет.'}{' '}
          <button type="button" className="link-btn" onClick={() => { setTone('all'); setQuery(''); }}>
            Показать всех
          </button>
        </p>
      ) : (
        <table className="table table--personnel">
          <thead>
            <tr>
              <th>Работник</th>
              <th>Допуск к работе</th>
              {DECISIONS.map((decision) => (
                <th key={decision.key}>{decision.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => {
              const rowToneValue = rowTone(row);
              return (
                <tr
                  key={row.id}
                  className={`personnel-row personnel-row--${rowToneValue}${
                    openedId === row.id ? ' personnel-row--opened' : ''
                  }`}
                  aria-current={openedId === row.id ? 'true' : undefined}
                  onClick={() => setOpenedId(row.id)}
                >
                  <td>
                    {/* Кнопка, а не только клик по строке: строку должно быть
                        можно открыть и с клавиатуры. */}
                    <button type="button" className="personnel-row__name" onClick={() => setOpenedId(row.id)}>
                      {row.user.name}
                    </button>
                    <span className="personnel-row__sub">
                      {row.employeeNumber ?? 'таб. № не заведён'} · {subdivisionCell(row)}
                    </span>
                    {withoutBasisCount(row) > 0 ? (
                      <span className="personnel-row__no-basis">
                        Допусков без документа-основания: {withoutBasisCount(row)}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <span className={`status-word status-word--${rowToneValue}`}>
                      {row.isActive ? admissionLabel(row) : 'Не работает'}
                    </span>
                  </td>
                  {DECISIONS.map((decision) => {
                    const clearance = decisionClearance(row, decision.key);
                    if (!clearance) {
                      return (
                        <td key={decision.key} className="personnel-row__none">не заведён</td>
                      );
                    }
                    return (
                      <td key={decision.key}>
                        <span className={termClass(clearance)}>
                          {decision.key === 'electrical' && clearance.electricalSafetyGroup
                            ? `группа ${clearance.electricalSafetyGroup} · `
                            : ''}
                          {relativeTerm(clearance, today)}
                        </span>
                        {clearance.expiresAt ? (
                          <span className="term__date">{formatDate(clearance.expiresAt)}</span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {noRequirements ? (
        <p className="section__note">
          Обязательные допуски в организации не заведены, поэтому оснований в списке нет, а к работе никто
          не допущен. Заводит их инженер по охране труда.
        </p>
      ) : null}

      {opened ? (
        <WorkerCard
          row={opened}
          token={token}
          organizationId={organizationId}
          canManageClearances={mayManageClearanceEvidence(capabilities)}
          canEditPosition={mayEditPosition(capabilities)}
          onClose={() => setOpenedId(null)}
          // Внесённый допуск меняет и строку в таблице: допущен ли работник
          // теперь, считает сервер, и список спрашивает это у него заново.
          onChanged={people.reload}
          onUnauthorized={onUnauthorized}
        />
      ) : null}
    </>
  );
}
